import React, { useState } from "react";
import { AcmeCoOperationErrorIcon, TooltipsIcon } from "../../assets/icons";
import axios from "axios";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { reqToUpdateSettings } from "../../redux/slices.js/settings/settingsSlice";

const CancellationRules = ({ settings = [] }) => {
  const dispatch = useDispatch();
  // Convert API array → object { name: value }
  const initialState = settings.reduce((acc, s) => {
    acc[s.name] = s.value;
    return acc;
  }, {});

  const [formValues, setFormValues] = useState({
    allow_self_service_cancellation: false,
    require_cancellation_reason: false,
    show_exit_survey: false,
    show_retention_offers: false,
    allow_subscription_pausing: false,
    show_exit_survey_2: false,
    min_notice_period_days: "",
    max_pause_duration_days: "",
    ...initialState,
  });

  const selfServiceToggles = [
    {
      id: "allow_self_service_cancellation",
      label: "Allow self-service cancellation",
    },
    { id: "require_cancellation_reason", label: "Require cancellation reason" },
    { id: "show_exit_survey", label: "Show exit survey" },
  ];

  const retentionToggles = [
    { id: "show_retention_offers", label: "Show retention offers" },
    { id: "allow_subscription_pausing", label: "Allow subscription pausing" },
    { id: "show_exit_survey_2", label: "Show exit survey" },
  ];

  const handleToggle = (id) => {
    setFormValues((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveChanges = () => {
    const payload = Object.entries(formValues).map(([name, value]) => ({
      name,
      value: value instanceof Date ? value.toISOString() : value,
    }));

    dispatch(
      reqToUpdateSettings({
        data: payload,
        onSuccess: (response) => {
          // any additional success logic here
        },
        onFailure: (error) => {
          console.error("Failed to update settings:", error);
          // any additional failure logic here
        },
      })
    );
  };

  return (
    <>
      <div className="space-y-6">
        <div className="bg-[#EDF7FF] rounded-xl sm:p-6 p-3">
          <div className="flex flex-col xl:flex-row md:items-start gap-4 md:justify-between">
            <div className="flex items-center gap-3">
              <span className="min-[40px]">{AcmeCoOperationErrorIcon}</span>
              <div>
                <span className="text-[16px] font-semibold mb-2 block">
                  Subscription Cancellation Policies
                </span>
                <p className="text-[16px] text-gray-500 text-sm mb-0">
                  Configure cancellation workflows, retention strategies, and
                  pause options
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Self-Service Options */}
        <div className="form-group">
          <label className="!text-black pb-2">Self-Service Options</label>
          <div className="grid gap-4">
            {selfServiceToggles.map((toggle) => (
              <div
                key={toggle.id}
                className="flex items-center justify-start gap-4"
              >
                <button
                  type="button"
                  role="switch"
                  aria-checked={formValues[toggle.id]}
                  onClick={() => handleToggle(toggle.id)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    formValues[toggle.id] ? "bg-[#4285F4]" : "bg-[#E5E7EB]"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formValues[toggle.id]
                        ? "translate-x-[10px]"
                        : "translate-x-0"
                    }`}
                  />
                </button>
                <span className="text-black text-span">{toggle.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Retention Options */}
        <div className="form-group">
          <label className="!text-black pb-2">Retention Options</label>
          <div className="grid gap-4">
            {retentionToggles.map((toggle) => (
              <div
                key={toggle.id}
                className="flex items-center justify-start gap-4"
              >
                <button
                  type="button"
                  role="switch"
                  aria-checked={formValues[toggle.id]}
                  onClick={() => handleToggle(toggle.id)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    formValues[toggle.id] ? "bg-[#4285F4]" : "bg-[#E5E7EB]"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formValues[toggle.id]
                        ? "translate-x-[10px]"
                        : "translate-x-0"
                    }`}
                  />
                </button>
                <span className="text-black text-span">{toggle.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Input Fields */}
        <div className="grid gap-4 md:gap-6 md:grid-cols-2">
          <div className="form-group">
            <label>
              Minimum Notice Period (days) <span>*</span>
            </label>
            <div className="relative">
              <input
                type="number"
                placeholder="90 days"
                className="pe-5"
                value={formValues.min_notice_period_days}
                onChange={(e) =>
                  handleChange("min_notice_period_days", e.target.value)
                }
              />
              <span className="absolute right-3 top-3 h-5 w-5 text-gray-500 pointer-events-none">
                {TooltipsIcon}
              </span>
            </div>
          </div>

          <div className="form-group">
            <label>
              Max Pause Duration (days) <span>*</span>
            </label>
            <input
              type="number"
              placeholder="90 days"
              value={formValues.max_pause_duration_days}
              onChange={(e) =>
                handleChange("max_pause_duration_days", e.target.value)
              }
            />
          </div>
        </div>
      </div>

      <div className={`mt-auto flex gap-3 pt-6 justify-end flex-wrap`}>
        <button type="button" className="next-btn" onClick={handleSaveChanges}>
          Save Cancellation Rules
        </button>
      </div>
    </>
  );
};

export default CancellationRules;
